import { upperFirst } from 'lodash-es'

import { Icon } from '@fv/client-components'
import type { QuoteCharge } from '@fv/client-types'
import type { Currency, ExchangeRateMap } from '@fv/models'

import { List, ListItem } from '@/components/List'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { useCurrency } from '@/hooks/settings'
import { convertCurrency } from '@/hooks/useExchangeRate'

type rateChargesProps = {
  charges?: QuoteCharge[]
  amount: number
  rateCurrency?: Currency
  displayCurrency?: Currency | null
  exchangeRates?: ExchangeRateMap
}

export const RateCharges = ({
  charges,
  amount,
  rateCurrency,
  displayCurrency,
  exchangeRates,
}: rateChargesProps) => {
  const sortedCharges = sortCharges(charges)
  const priceParts = useCurrency()

  const convert = (val: number) =>
    displayCurrency && exchangeRates && rateCurrency
      ? convertCurrency(val, rateCurrency, displayCurrency, exchangeRates)
      : { amount: val, currency: rateCurrency }

  const total = convert(amount)
  const [dollars, cents] = priceParts(total.amount)
  const currencyLabel = total.currency?.toUpperCase() ?? null

  const generateToolTip = (name: string, desc: string | undefined) => {
    const ignoreList = ['linehaul', 'discount', 'base rate', 'fuel']
    if (!ignoreList.includes(name) && desc) {
      return (
        <ShipperTooltip label={desc}>
          <Icon icon="question-circle" className="text-fv-orange ml-1" />
        </ShipperTooltip>
      )
    }
    return null
  }

  return (
    <List fcp0 lcp0 className="p-6">
      {sortedCharges?.map(c => {
        const [dollars, cents] = priceParts(convert(c.amount).amount)
        const name =
          c.code === 'FVMU' && c.providerDetail ? c.providerDetail : c.name

        return (
          <ListItem
            className="!border-fv-green-300 border-dashed"
            key={`c.name-${c.amount}`}
          >
            <div className="pr-4">
              {name ? upperFirst(name) : c.code === 'OTR' ? 'Other' : c.code}
              {generateToolTip(name, c.providerDetail)}
            </div>
            <div className="ml-auto font-mono">
              {c.name === 'discount' && '-'}
              {dollars}.{cents}
            </div>
          </ListItem>
        )
      })}

      <ListItem className="!border-fv-green-300">
        <div className="pr-4">
          Total cost of shipment{' '}
          <Icon
            icon="arrow-right"
            className="text-fv-gray-dark"
            transform="down-.5"
          />
        </div>
        <div className="ml-auto font-mono">
          ${dollars}.{cents}
          {currencyLabel && (
            <span className="text-xs ml-1 text-fv-gray-dark">
              {currencyLabel}
            </span>
          )}
        </div>
      </ListItem>
    </List>
  )
}

function sortCharges(charges?: QuoteCharge[]): QuoteCharge[] {
  if (!charges) return []

  return charges.slice().sort((a, b) => {
    if (a.name === 'linehaul') return -1
    if (b.name === 'linehaul') return 1
    if (a.name === 'base rate') return -1
    if (b.name === 'base rate') return 1
    if (a.name === 'discount') return -1
    if (b.name === 'discount') return 1
    if (a.name === 'fuel') return -1
    if (b.name === 'fuel') return 1
    return 0
  })
}
